/**
 * API methods for canonical book matching, chunk processing, chunk cache queries,
 * and Lens explanation cloud sync.
 * Mixin pattern — these functions use `this` and are assigned to ApiClient.prototype.
 */

import * as localDB from '../localDB';
import { generateId } from '../utils';
import { hammingDistance } from '../simhash';
import { API_BASE } from './helpers';
import { ensureDB, getDB } from '../db/helpers';
import {
  isProtected,
  shouldInvalidateLocal,
  shouldSkipCloudResult,
  isUpgradeCandidate,
} from '../chunks/freshness';

/** Last known min_prompt_version — skip redundant prune scans. */
let _lastPrunedVersion = null;

const LS_MIN_VERSION_KEY = 'ev_min_prompt_version';

/** Get the minimum prompt version (from memory or localStorage). */
export function getMinPromptVersion() {
  if (_lastPrunedVersion) return _lastPrunedVersion;
  try { return localStorage.getItem(LS_MIN_VERSION_KEY); } catch { return null; }
}

/**
 * Delete locally cached chapter_chunks whose prompt_version is older than minVersion.
 * Runs at most once per version per session (idempotent).
 */
async function pruneStaleLocalChunks(minVersion) {
  if (!minVersion || minVersion === _lastPrunedVersion) return;
  _lastPrunedVersion = minVersion;
  try { localStorage.setItem(LS_MIN_VERSION_KEY, minVersion); } catch {}
  try {
    /* ensureDB / getDB — static import at top */
    if (!(await ensureDB())) return;
    const db = getDB();
    if (!db.objectStoreNames.contains('chapter_chunks')) return;
    const tx = db.transaction('chapter_chunks', 'readwrite');
    const store = tx.objectStore('chapter_chunks');
    const cursor = store.openCursor();
    cursor.onsuccess = (e) => {
      const c = e.target.result;
      if (c) {
        if (shouldInvalidateLocal(c.value, minVersion)) {
          c.delete();
        }
        c.continue();
      }
    };
  } catch { /* best-effort */ }
}

/**
 * Match or create a canonical book record.
 * Tries: ISBN → SimHash → Title+Author → create new.
 * Stores result in both cloud (via API) and local IndexedDB.
 *
 * @param {{ isbn?: string, simhash?: string, title: string, author?: string, language?: string, totalChapters?: number }} bookInfo
 * @returns {Promise<{ canonical_book_id: string, match_method: string }>}
 */
export async function matchCanonicalBook(bookInfo) {
  const { isbn, simhash, title, author, language, totalChapters } = bookInfo;

  // Try cloud matching first (has all users' data)
  if (this.token) {
    try {
      const resp = await fetch(`${API_BASE}/reader/canonical-books/match`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isbn, simhash, title, author, language, total_chapters: totalChapters }),
      });
      if (resp.ok) {
        const result = await resp.json();
        // Cache in local IndexedDB (include chunks_public for offline gate)
        await localDB.putCanonicalBook({
          canonical_book_id: result.canonical_book_id,
          isbn, simhash, title, author, language,
          total_chapters: totalChapters || 0,
          chunks_public: result.chunks_public ?? 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
        return result;
      }
    } catch {
      // Fall through to local matching
    }
  }

  // Offline fallback: local matching
  const allLocal = await localDB.getAllCanonicalBooks();

  // 1. ISBN match
  if (isbn) {
    const match = allLocal.find(b => b.isbn === isbn);
    if (match) return { canonical_book_id: match.canonical_book_id, match_method: 'isbn', chunks_public: match.chunks_public ?? 0 };
  }

  // 2. SimHash match
  if (simhash) {
    for (const b of allLocal) {
      if (!b.simhash) continue;
      const dist = hammingDistance(simhash, b.simhash);
      if (dist <= 10) {
        return { canonical_book_id: b.canonical_book_id, match_method: 'simhash', hamming_distance: dist, chunks_public: b.chunks_public ?? 0 };
      }
    }
  }

  // 3. Title+Author match (case-insensitive)
  if (title) {
    const normTitle = title.toLowerCase().trim();
    const match = allLocal.find(b => {
      if (b.title?.toLowerCase().trim() !== normTitle) return false;
      if (!author || !b.author) return true; // if either has no author, title match is enough
      return b.author.toLowerCase().trim() === author.toLowerCase().trim();
    });
    if (match) return { canonical_book_id: match.canonical_book_id, match_method: 'title_author', chunks_public: match.chunks_public ?? 0 };
  }

  // 4. No match → create new
  const newId = generateId();
  const now = new Date().toISOString();
  await localDB.putCanonicalBook({
    canonical_book_id: newId,
    isbn: isbn || null,
    simhash: simhash || null,
    title, author: author || '',
    language: language || null,
    total_chapters: totalChapters || 0,
    chunks_public: 0,
    created_at: now, updated_at: now,
  });
  return { canonical_book_id: newId, match_method: 'created_new', chunks_public: 0 };
}

/**
 * Query chapter chunks from cloud (for hashes not found locally).
 *
 * Side effect — §F17 stale-EPUB detection: if backend reports that any
 * queried hash is missing chunks but the SAME (canonical_book_id,
 * chapter_index) has chunks under a DIFFERENT hash (via the new
 * `chapter_registry_alt_hashes` field), dispatches a window event
 * `ev:stale-epub-detected`. Listeners (useChunkAnnotations) handle the
 * sync-before-delete cache eviction. We emit instead of return-changing
 * to keep the existing signature; callers that don't care are unaffected.
 *
 * @param {string[]} hashes - Array of chapter_content_hash values
 * @returns {Promise<Map<string, object>>} Map of hash → chunk record
 */
/**
 * Per-user chunk hint override fetch.
 *
 * Returns a Map<chunkText, {hint_zh, hint_en, model, updated_at}> for chunks
 * within (canonicalBookId, hashes) that the user has previously 👎 → rehinted.
 * Empty map on miss / anonymous / network error (override fetch is best-effort
 * — chunk render must still work if this fails).
 *
 * Called by useChunkAnnotations after the chunks JSON is loaded; the hook
 * merges hint_zh / hint_en from the override map into each chunk before
 * locating + rendering.
 *
 * @param {string} canonicalBookId
 * @param {string[]} hashes - Array of chapter_content_hash values (typically 1)
 * @returns {Promise<Map<string, {hint_zh: string|null, hint_en: string|null, model: string|null, updated_at: string}>>}
 */
export async function fetchChunkHintOverrides(canonicalBookId, hashes) {
  if (!this.token || !canonicalBookId || !hashes?.length) return new Map();
  try {
    const url = `${API_BASE}/reader/chunk-hint-overrides?canonical_book_id=${encodeURIComponent(canonicalBookId)}&hashes=${encodeURIComponent(hashes.join(','))}`;
    const resp = await fetch(url, {
      headers: { 'Authorization': `Bearer ${this.token}` },
    });
    if (!resp.ok) return new Map();
    const data = await resp.json();
    const map = new Map();
    for (const o of (data.overrides || [])) {
      // Key by chunk_text — same dedup as chapter_chunks JSON. Within a
      // chapter, chunk_text is unique (preheat / realtime pipeline dedupes).
      map.set(o.chunk_text, {
        hint_zh: o.hint_zh,
        hint_en: o.hint_en,
        model: o.model,
        updated_at: o.updated_at,
      });
    }
    return map;
  } catch {
    return new Map();
  }
}

export async function fetchChapterChunksFromCloud(hashes) {
  if (!this.token || !hashes.length) return new Map();
  try {
    const resp = await fetch(`${API_BASE}/reader/chapter-chunks?hashes=${hashes.join(',')}`, {
      headers: { 'Authorization': `Bearer ${this.token}` },
    });
    if (!resp.ok) return new Map();
    const data = await resp.json();
    const results = new Map();

    const minVersion = data.min_prompt_version;
    // NOTE: we deliberately do NOT prune stale local chunks here anymore. The
    // backend refuses to auto-upgrade stale-prompt-version records (returns the
    // same old chunks to "protect user data"), so pruning them just forces a
    // refetch of identical stale data → a delete/refetch loop on every reopen.
    // Real upgrades arrive via reconcileBookChunks (cloud-driven, protection-
    // aware). shouldSkipCloudResult below still uses minVersion to avoid caching
    // a stale CLOUD record into the local store.

    for (const record of (data.chunks || [])) {
      // Stale records fall through to POST /chunks/process which triggers reprocessing.
      // In-flight records (status='processing') are kept for progressive loading.
      // Protected records (opus / admin-passed) bypass staleness entirely.
      if (shouldSkipCloudResult(record, minVersion)) continue;
      results.set(record.chapter_content_hash, record);
      // Only cache complete results locally (partial results must re-fetch from cloud)
      if (!record.status || record.status === 'complete') {
        await localDB.putChapterChunks(record);
      }
    }

    // §F17 stale-EPUB detection. alt_hashes is { [chapter_index]: [hash...] }
    // — backend already filtered to alternates with status='complete', so a
    // non-empty value here means valid replacement chunks exist for the same
    // chapter under a different hash. Symptom of a pre-Mistral local EPUB
    // whose hash diverged from what the canonical now has.
    //
    // Don't dispatch if no hash was missing (all queries resolved), because
    // alt_hashes can be empty-but-present and we only care about misses.
    const altHashes = data.chapter_registry_alt_hashes || {};
    const missingHashes = hashes.filter(h => !results.has(h));
    if (missingHashes.length > 0 && Object.keys(altHashes).length > 0) {
      try {
        window.dispatchEvent(new CustomEvent('ev:stale-epub-detected', {
          detail: {
            missing_hashes: missingHashes,
            chapter_index_alts: altHashes,
            // Source — useful for telemetry / debugging. Listener filters by
            // current open book before acting (see useChunkAnnotations).
            origin: 'fetchChapterChunksFromCloud',
          },
        }));
      } catch { /* SSR / non-browser — ignore */ }
    }

    return results;
  } catch {
    return new Map();
  }
}

/**
 * Request LLM chunk processing for a chapter via backend.
 *
 * Plan gating: chunks are a Pro+ service. Free/Standard users hit
 * `/reader/chunks/process` and get 403. We surface that as
 * `{ status: 'plan_gated' }` so callers can short-circuit polling instead
 * of spinning for 24×5s = 2 min on a request that will never succeed
 * (§F8 silent-spinner fix).
 *
 * @param {{ chapter_text: string, language: string, chapter_content_hash: string, canonical_book_id: string, chapter_index: number }} params
 * @returns {Promise<object|null>} Chunk result, `{ status: 'plan_gated' }`, or null on failure
 */
export async function processChapterChunks(params) {
  if (!this.token) return null;
  try {
    const resp = await fetch(`${API_BASE}/reader/chunks/process`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });
    if (resp.status === 202) {
      return { status: 'processing' };
    }
    if (resp.status === 403) {
      return { status: 'plan_gated' };
    }
    if (!resp.ok) return null;
    const result = await resp.json();
    // Only cache complete results locally (partial results must re-fetch from cloud)
    if (result.chapter_content_hash && (!result.status || result.status === 'complete')) {
      await localDB.putChapterChunks(result);
    }
    return result;
  } catch {
    return null;
  }
}

/**
 * Upload chapter registry entries to cloud.
 * @param {Array} entries
 */
export async function uploadChapterRegistry(entries) {
  if (!this.token || !entries.length) return;
  try {
    await fetch(`${API_BASE}/reader/chapter-registry`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ entries }),
    });
  } catch { /* best-effort */ }
}

/**
 * Upload browser-extracted chapter texts to cloud (permanent KV cache).
 * Called by preheat to ensure audit-export returns browser text, not linkedom text.
 * @param {Array<{chapter_content_hash: string, chapter_text: string}>} entries
 */
export async function uploadBrowserTexts(entries) {
  if (!this.token || !entries?.length) return;
  const resp = await fetch(`${API_BASE}/reader/browser-texts`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${this.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ entries }),
  });
  if (!resp.ok) throw new Error(`uploadBrowserTexts ${resp.status}`);
}

/**
 * Fetch chapter registry from cloud for a canonical book.
 * @param {string} canonicalBookId
 * @returns {Promise<Array>}
 */
export async function fetchChapterRegistry(canonicalBookId) {
  if (!this.token) return [];
  try {
    const resp = await fetch(`${API_BASE}/reader/chapter-registry/${canonicalBookId}`, {
      headers: { 'Authorization': `Bearer ${this.token}` },
    });
    if (!resp.ok) return [];
    const data = await resp.json();
    // Cache locally
    if (data.entries?.length) {
      await localDB.putChapterRegistryBatch(data.entries);
    }
    return data.entries || [];
  } catch {
    return [];
  }
}

/**
 * Query chunk explanation from cloud (three-level fallback: chapter → book → any).
 * Caches the result in local IndexedDB on hit.
 * @param {string} text - Chunk text
 * @param {string} srcLang - Source language
 * @param {string} tgtLang - Target language
 * @param {string|null} chapterHash - Chapter content hash (for exact match)
 * @param {string|null} canonicalBookId - Canonical book ID (for same-book match)
 * @returns {Promise<object|null>} Explanation record or null
 */
export async function fetchChunkExplanationFromCloud(text, srcLang, tgtLang, chapterHash, canonicalBookId) {
  if (!this.token) return null;
  try {
    const params = new URLSearchParams({ text, src_lang: srcLang, tgt_lang: tgtLang });
    if (chapterHash) params.set('chapter_hash', chapterHash);
    if (canonicalBookId) params.set('book_id', canonicalBookId);
    const resp = await fetch(`${API_BASE}/reader/chunk-explanations?${params}`, {
      headers: { 'Authorization': `Bearer ${this.token}` },
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    if (data.explanation) {
      // Cache in local IndexedDB for future offline access
      await localDB.putChunkExplanation(data.explanation);
    }
    return data.explanation;
  } catch {
    return null;
  }
}

/**
 * Upload a Lens explanation to cloud for cross-device/cross-user reuse.
 * Fire-and-forget — failures are silently ignored.
 * @param {object} data - Explanation record (must include id and chunk_text)
 */
export async function uploadChunkExplanationToCloud(data) {
  if (!this.token || !data?.id || !data?.chunk_text) return;
  try {
    await fetch(`${API_BASE}/reader/chunk-explanations`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  } catch { /* best-effort */ }
}

/**
 * Fetch user chunk profile from D1 (cross-device sync).
 * @param {string} lang - Language code (e.g. 'en')
 * @returns {Promise<object|null>} Profile record or null
 */
export async function fetchChunkProfile(lang) {
  if (!this.token || !lang) return null;
  try {
    const resp = await fetch(`${API_BASE}/reader/chunk-profile?lang=${encodeURIComponent(lang)}`, {
      headers: { 'Authorization': `Bearer ${this.token}` },
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    return data.profile || null;
  } catch {
    return null;
  }
}

/**
 * Update manual_level in D1 chunk profile.
 * Fire-and-forget — failures are silently ignored.
 * @param {string} lang - Language code
 * @param {string|null} manualLevel - CEFR level or 'ALL' or null
 */
export async function updateChunkProfileManualLevel(lang, manualLevel) {
  if (!this.token || !lang) return;
  try {
    await fetch(`${API_BASE}/reader/chunk-profile`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ lang, manual_level: manualLevel }),
    });
  } catch { /* best-effort */ }
}

/**
 * Delete user chunk profile from D1 (full reset).
 * Fire-and-forget — failures are silently ignored.
 * @param {string} lang - Language code
 */
export async function deleteChunkProfile(lang) {
  if (!this.token || !lang) return;
  try {
    await fetch(`${API_BASE}/reader/chunk-profile?lang=${encodeURIComponent(lang)}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${this.token}` },
    });
  } catch { /* best-effort */ }
}

/**
 * Delete a cached Lens explanation from D1 (user-reported bad translation).
 * Fire-and-forget — failures are silently ignored.
 * @param {string} text - Chunk text
 * @param {string} srcLang - Source language
 * @param {string} tgtLang - Target language
 */
export async function deleteChunkExplanationFromCloud(text, srcLang, tgtLang) {
  if (!this.token || !text) return;
  try {
    const params = new URLSearchParams({ text, src_lang: srcLang, tgt_lang: tgtLang });
    await fetch(`${API_BASE}/reader/chunk-explanations?${params}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${this.token}` },
    });
  } catch { /* best-effort */ }
}

/**
 * Report a chunk annotation event for feedback loop analytics.
 * Fire-and-forget — failures are silently ignored.
 * @param {{ annotation_text: string, annotation_category: string, annotation_type: string, annotation_level: string, action: 'dot_click'|'hint_dismiss'|'lens_trigger', prompt_version?: string, model?: string, book_id?: string, chapter_index?: number, cached_translation?: string, canonical_book_id?: string, chapter_content_hash?: string, target_language?: string, source_language?: string, sentence?: string }} data
 */
export async function reportChunkEvent(data) {
  if (!this.token || !data?.annotation_text || !data?.action) return;
  try {
    await fetch(`${API_BASE}/reader/chunk-events`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'application/json',
        'X-EaseVerse-Client': 'reader',
      },
      body: JSON.stringify(data),
    });
  } catch { /* best-effort */ }
}

/**
 * Record a passive lookup event (Reader chunk dot click → hint cached, no LLM call).
 * Writes to user_lookup_log (L1) + word_lookups (L2, authenticated only).
 *
 * Fire-and-forget. The response also carries chip/quiz signals (Phase 3); for
 * now the caller can ignore the body — frontends will start consuming
 * promote_chip_eligible / echo_quiz_due once the recall UI lands.
 *
 * @param {{
 *   word: string,
 *   language?: string,
 *   target_language?: string,
 *   sentence?: string,
 *   hint?: string,
 *   cefr_level?: string,
 *   category?: string,
 *   type?: string,
 *   book_id?: string,
 *   canonical_book_id?: string,
 *   chapter_index?: number,
 *   chapter_content_hash?: string,
 *   prompt_version?: string,
 *   llm_model?: string,
 *   cache_hit?: 0|1|2,
 *   entry_point?: string,
 * }} data
 */
export async function trackLookup(data) {
  if (!this.token || !data?.word) return;
  try {
    await fetch(`${API_BASE}/lookup-recall/track`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'application/json',
        'X-EaseVerse-Client': 'reader',
      },
      body: JSON.stringify(data),
    });
  } catch { /* best-effort */ }
}

/**
 * Submit A/B test translation quality feedback (thumbs up/down).
 * Uses the existing /ab-rating endpoint.
 * @param {{ model: string, rating: 'up'|'down', inputText: string, context?: string, targetLang?: string, mode?: string, translationOutput?: string, translationId?: string, latencyMs?: number }} data
 */
export async function submitAbRating(data) {
  if (!this.token || !data?.inputText || !data?.rating) return;
  try {
    await fetch(`${API_BASE}/ab-rating`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  } catch { /* best-effort */ }
}

/**
 * Reconcile local cached chunks against cloud for a book.
 * Triggered once-per-session per book on open.
 *
 * Problem: the three-tier fallback in useChunkAnnotations (getChunks) hits IndexedDB
 * first and returns immediately on cache hit. When backend upgrades chunks from
 * gemini+deepseek to claude-opus (same prompt_version), the existing staleness check
 * doesn't detect the upgrade — local Gemini stays sticky forever.
 *
 * This function walks local "upgradeable" records (non-opus, non-admin-passed) and
 * asks the cloud whether there's a better version. fetchChapterChunksFromCloud()
 * already overwrites local IndexedDB via putChapterChunks for each complete record
 * (see chunkApi.js:174-177), so no extra write logic is needed here.
 *
 * @param {string} canonicalBookId
 * @returns {Promise<{ checked: number, refreshed: number }>}
 */
export async function reconcileBookChunks(canonicalBookId) {
  if (!this.token || !canonicalBookId) return { checked: 0, refreshed: 0 };
  try {
    const allLocal = await localDB.getChapterChunksByBookId(canonicalBookId);
    const upgradeable = allLocal.filter(isUpgradeCandidate);
    if (upgradeable.length === 0) return { checked: 0, refreshed: 0 };

    const hashes = upgradeable.map(r => r.chapter_content_hash);
    // Backend GET /reader/chapter-chunks caps at 100 hashes (chunks.ts:103).
    // Large books (100+ chapters) must be paged or refresh silently drops suffix records.
    const BATCH = 100;
    let refreshed = 0;
    for (let i = 0; i < hashes.length; i += BATCH) {
      const batch = hashes.slice(i, i + BATCH);
      const cloudResults = await this.fetchChapterChunksFromCloud(batch);
      for (const [, record] of cloudResults) {
        if (isProtected(record)) refreshed++;
      }
    }

    // Telemetry: report success only when something actually refreshed (reduces noise).
    // Fire-and-forget; VALID_ACTIONS whitelist on backend (chunks.ts:1660).
    if (refreshed > 0) {
      this.reportChunkEvent({
        annotation_text: `reconcile:${refreshed}/${upgradeable.length}`,
        action: 'chunk_reconcile_ok',
        book_id: canonicalBookId,
      });
    }

    return { checked: upgradeable.length, refreshed };
  } catch (e) {
    this.reportChunkEvent({
      annotation_text: `reconcile_err:${String(e?.message || e).slice(0, 200)}`,
      action: 'chunk_reconcile_fail',
      book_id: canonicalBookId,
    }).catch(() => { /* best-effort */ });
    return { checked: 0, refreshed: 0 };
  }
}

/**
 * Register a book for background preheat via Claude Code skill.
 * Fire-and-forget — backend stores in KV queue for later processing.
 * @param {{ canonical_book_id: string, language: string }} params
 */
export async function triggerPreheatRequest({ canonical_book_id, language }) {
  if (!this.token || !canonical_book_id) return;
  try {
    await fetch(`${API_BASE}/reader/chunks/preheat-request`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ canonical_book_id, language }),
    });
  } catch { /* fire-and-forget */ }
}
